/**
 * PUDO Inventory - View all packages currently at this PUDO location
 * Shows detailed inventory with filtering and actions
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  ArrowLeft,
  Package,
  Search,
  RefreshCw,
  Users,
  Truck,
  Clock,
  Filter,
  ChevronRight,
  MapPin
} from 'lucide-react';

const PudoInventory = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [packages, setPackages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // all, waiting_customer, waiting_driver

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      const response = await api.get('/pudo/dashboard');
      // Combine waiting and incoming packages
      const allPackages = [
        ...(response.data.waiting_packages || []).map(p => ({ ...p, type: 'waiting_customer' })),
        ...(response.data.incoming_packages || []).map(p => ({ ...p, type: 'incoming' }))
      ];
      setPackages(allPackages);
    } catch (error) {
      console.error('Failed to load inventory:', error);
      if (error.response?.status === 403) {
        toast.error('PUDO staff access required');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchInventory();
    setRefreshing(false);
    toast.success('Inventory refreshed');
  };

  const filteredPackages = packages.filter(pkg => {
    // Search filter
    const matchesSearch = !searchTerm || 
      pkg.package_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pkg.tracking_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pkg.recipient_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Type filter
    const matchesFilter = filter === 'all' || pkg.type === filter;
    
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin h-10 w-10 border-4 border-purple-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="pudo-inventory-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/pudo')}
              className="p-2 hover:bg-gray-100 rounded-full"
              data-testid="back-button"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Package className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-800">Inventory</h1>
                <p className="text-xs text-gray-500">{packages.length} packages</p>
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2"
            data-testid="refresh-button"
          >
            <RefreshCw className={`h-5 w-5 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Search & Filter */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by ID, tracking, or recipient..."
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 bg-gray-100 p-1 rounded-xl" data-testid="filter-tabs">
          <button
            onClick={() => setFilter('all')}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
              filter === 'all' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-600'
            }`}
          >
            All ({packages.length})
          </button>
          <button
            onClick={() => setFilter('waiting_customer')}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
              filter === 'waiting_customer' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-600'
            }`}
          >
            <Users className="h-4 w-4 inline mr-1" />
            For Pickup ({packages.filter(p => p.type === 'waiting_customer').length})
          </button>
          <button
            onClick={() => setFilter('incoming')}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
              filter === 'incoming' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-600'
            }`}
          >
            <Truck className="h-4 w-4 inline mr-1" />
            Incoming ({packages.filter(p => p.type === 'incoming').length})
          </button>
        </div>

        {/* Package List */}
        {filteredPackages.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center border border-gray-100">
            <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">
              {searchTerm ? 'No packages match your search' : 'No packages in inventory'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredPackages.map((pkg, idx) => (
              <div 
                key={pkg.package_id || idx}
                className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
                data-testid={`inventory-item-${idx}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div className={`p-2 rounded-lg ${
                      pkg.type === 'waiting_customer' ? 'bg-green-100' : 'bg-blue-100'
                    }`}>
                      {pkg.type === 'waiting_customer' ? (
                        <Users className="h-5 w-5 text-green-600" />
                      ) : (
                        <Truck className="h-5 w-5 text-blue-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-800 truncate">
                          {pkg.tracking_code || pkg.package_id?.slice(0, 12)}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          pkg.type === 'waiting_customer' 
                            ? 'bg-green-100 text-green-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {pkg.type === 'waiting_customer' ? 'For Customer' : 'Incoming'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 truncate">
                        {pkg.type === 'waiting_customer' 
                          ? `To: ${pkg.recipient_name || 'Customer'}`
                          : `From: ${pkg.from_driver_name || 'Driver'}`
                        }
                      </p>
                      {pkg.arrived_at && (
                        <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                          <Clock className="h-3 w-3" />
                          Arrived: {new Date(pkg.arrived_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {pkg.type === 'waiting_customer' ? (
                    <Button
                      size="sm"
                      onClick={() => navigate('/pudo/release', { state: { packageId: pkg.package_id } })}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`release-btn-${idx}`}
                    >
                      Release
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate('/pudo/receive')}
                      className="border-blue-200 text-blue-600 hover:bg-blue-50"
                      data-testid={`receive-btn-${idx}`}
                    >
                      Receive
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default PudoInventory;
